






















































import {Vue, Component, Prop, Watch} from 'vue-property-decorator'
import OrbitRoundScrollableTable from '@/components/orbit/OrbitRoundScrollableTable.vue'
import OrbitRoundPoolSummary from '@/components/orbit/OrbitRoundPoolSummary.vue'
import FilterPreviousRoundOrbit from '@/components/filters/FilterPreviousRoundOrbit.vue'
import OrbitClaimModal, {
  OrbitIndividualReward,
} from '@/components/orbit/OrbitClaimModal.vue'
import OrbitClaimBar, {
  OrbitClaimBarItem,
} from '@/components/orbit/OrbitClaimBar.vue'
import {$} from '@/facade'
import {OrbitProjectCollection} from '@/model/collection/OrbitProjectCollection'
import {OrbitRoundRewardCollection} from '@/model/collection/OrbitRoundRewardCollection'
import {OrbitTransactionCollection} from '@/model/collection/OrbitTransactionCollection'
import {OrbitRoundReward} from '@/model/resource/OrbitRoundReward'
import {OrbitHelper} from '@/helpers/OrbitHelper'

@Component({
  computed: {
    $() {
      return $
    },
  },
  components: {
    OrbitClaimBar,
    OrbitClaimModal,
    FilterPreviousRoundOrbit,
    OrbitRoundScrollableTable,
    OrbitRoundPoolSummary,
  },
})
export default class OrbitPreviousRoundContent extends Vue {
  @Prop({type: Number, required: true}) lastRound!: number
  @Prop({type: Number, required: false}) currentRound?: number

  orbitRewardCollection = new OrbitRoundRewardCollection()
  orbitProjectCollection = new OrbitProjectCollection()

  computedRound = this.lastRound

  transactionCountMap: Record<
    number,
    {
      total: string
      your: string
    }
  > = {}

  orbitClaimBarItems: OrbitClaimBarItem[] = []
  orbitIndividualRewards: OrbitIndividualReward[] = []
  unclaimedRewards: OrbitRoundReward[] = []
  showClaimModal = false
  isPopulatingPreviousRound = false
  roundClaimEndMs: number | null = null
  claimEndByRound: Record<number, number> = {}
  nowMs = Date.now()
  claimDeadlineInterval: number | null = null

  get hasNoUnclaimedRewards(): boolean {
    return this.unclaimedRewards.length === 0
  }

  get hasApprovedRewards(): boolean {
    return (
      this.orbitRewardCollection.items.filter(
        item => item.status === 'approved' || item.status === 'claimed'
      ).length > 0
    )
  }

  get hasClaimedRewards(): boolean {
    return (
      this.orbitRewardCollection.items.filter(item => item.status === 'claimed')
        .length > 0
    )
  }

  get allRewardsClaimed(): boolean {
    return (
      this.hasApprovedRewards &&
      this.hasNoUnclaimedRewards &&
      this.hasClaimedRewards
    )
  }

  get earliestClaimDeadlineMs(): number | null {
    const unclaimed = this.unclaimedRewards
    if (unclaimed.length === 0) return this.roundClaimEndMs

    const now = this.nowMs
    let earliest: number | null = null
    for (const r of unclaimed) {
      const ms = this.getExpirationMs(r)
      if (ms != null && ms > now && (earliest == null || ms < earliest)) {
        earliest = ms
      }
    }
    return earliest ?? this.roundClaimEndMs
  }

  get roundClaimPeriodEnded(): boolean {
    return this.roundClaimEndMs != null && this.roundClaimEndMs <= this.nowMs
  }

  get hasRewardsWithExpiredClaim(): boolean {
    return this.orbitRewardCollection.items.some(
      reward =>
        reward.status === 'expired' ||
        (reward.status === 'approved' && this.isRewardExpired(reward))
    )
  }

  get hasClaimPeriodEnded(): boolean {
    if (this.roundClaimPeriodEnded) return true
    return this.hasRewardsWithExpiredClaim && this.unclaimedRewards.length === 0
  }

  get hasUnclaimableExpiredRewards(): boolean {
    return (
      this.hasRewardsWithExpiredClaim && this.orbitClaimBarItems.length === 0
    )
  }

  get showClaimBar(): boolean {
    if (this.currentRound === undefined) return false
    if (this.computedRound === this.currentRound) return false
    return (
      this.orbitClaimBarItems.length > 0 ||
      this.allRewardsClaimed ||
      this.hasRewardsWithExpiredClaim
    )
  }

  get isClaimDisabled(): boolean {
    return this.hasNoUnclaimedRewards || this.hasClaimPeriodEnded
  }

  get displayedClaimDeadlineMs(): number | null {
    if (this.isPopulatingPreviousRound) return null
    return this.earliestClaimDeadlineMs
  }

  get displayedClaimPeriodEnded(): boolean {
    if (this.isPopulatingPreviousRound) return false
    return this.roundClaimPeriodEnded
  }

  get claimPeriodEndsAt(): number | null {
    const unclaimed = this.orbitRewardCollection.items.filter(
      orbitRoundReward => orbitRoundReward.status === 'approved'
    )

    if (!unclaimed.length) return null

    let earliestExpiration: number | null = null

    for (const orbitRoundReward of unclaimed) {
      const ms = this.getExpirationMs(orbitRoundReward)
      if (ms === null) continue

      if (!earliestExpiration || ms < earliestExpiration) {
        earliestExpiration = ms
      }
    }

    return earliestExpiration
  }

  getExpirationMs(reward: OrbitRoundReward): number | null {
    return (
      OrbitHelper.claimEndMsForReward(reward, this.claimEndByRound) ??
      this.roundClaimEndMs
    )
  }

  isRewardExpired(reward: OrbitRoundReward): boolean {
    if (reward.status === 'expired') return true
    const ms = this.getExpirationMs(reward)
    return ms != null && ms <= this.nowMs
  }

  isRewardClaimable(reward: OrbitRoundReward): boolean {
    if (reward.status !== 'approved') return false
    return !this.isRewardExpired(reward)
  }

  startClaimDeadlineTicker() {
    this.stopClaimDeadlineTicker()
    this.claimDeadlineInterval = window.setInterval(() => {
      this.nowMs = Date.now()
    }, 1000)
  }

  stopClaimDeadlineTicker() {
    if (this.claimDeadlineInterval != null) {
      clearInterval(this.claimDeadlineInterval)
      this.claimDeadlineInterval = null
    }
  }

  mounted() {
    this.applyCollection()
    this.populateResources()
    this.startClaimDeadlineTicker()
  }

  beforeDestroy() {
    this.stopClaimDeadlineTicker()
  }

  applyCollection() {
    this.orbitProjectCollection.round = [this.lastRound]
    this.orbitProjectCollection.active = true
  }

  async populateResources() {
    this.isPopulatingPreviousRound = true
    this.$await.init('populatePreviousRound')

    try {
      await this.orbitProjectCollection.queryAsPage()
      await this.populateTransactionCountMap(
        $.walletAdapter.n3Address ?? undefined
      )
      await this.populateRoundClaimEnd()
      await this.populateOrbitClaimData()
    } finally {
      this.isPopulatingPreviousRound = false
      this.$await.done('populatePreviousRound')
    }
  }

  async populateTransactionCountMap(walletAddress?: string) {
    this.transactionCountMap = {}
    const promises = this.orbitProjectCollection.items.map(async item => {
      const projectId = item.idOrbitProjectPk

      const orbitTransactionCollection = new OrbitTransactionCollection()

      orbitTransactionCollection.round = this.computedRound
      orbitTransactionCollection.idOrbitProjectFk = projectId
      orbitTransactionCollection.limit = 1

      await orbitTransactionCollection.queryAsPage()

      const totalTransactions = String(orbitTransactionCollection.total)

      if (walletAddress) {
        orbitTransactionCollection.address = walletAddress

        await orbitTransactionCollection.queryAsPage()

        const totalYourTransactions = String(orbitTransactionCollection.total)

        this.$set(this.transactionCountMap, projectId, {
          total: totalTransactions,
          your: totalYourTransactions,
        })

        return
      }

      this.$set(this.transactionCountMap, projectId, {
        total: totalTransactions,
        your: 'N/A',
      })
    })

    await Promise.all(promises)
  }

  async populateRoundClaimEnd() {
    const roundRewardCollection = new OrbitRoundRewardCollection()
    roundRewardCollection.round = this.computedRound
    roundRewardCollection.limit = 1
    await roundRewardCollection.queryAsPage()

    const claimEndByRound = await OrbitHelper.buildClaimEndByRoundMap(
      roundRewardCollection.items || []
    )

    this.roundClaimEndMs =
      claimEndByRound[this.computedRound] ??
      (await OrbitHelper.resolveClaimEndTimeMs(this.computedRound, null))
  }

  async populateOrbitClaimData() {
    if (!$.walletAdapter.n3Address) {
      this.orbitRewardCollection.items = []
      this.orbitClaimBarItems = []
      this.orbitIndividualRewards = []
      this.unclaimedRewards = []
      this.showClaimModal = false
      this.claimEndByRound = {}
      return
    }

    this.orbitRewardCollection.address = $.walletAdapter.n3Address
    this.orbitRewardCollection.round = this.computedRound
    await this.orbitRewardCollection.queryAsPage()

    this.claimEndByRound = await OrbitHelper.buildClaimEndByRoundMap(
      this.orbitRewardCollection.items || []
    )

    await this.populateClaimables()
    this.populateOrbitClaimBar()
    await this.populateOrbitClaimAmount()
  }

  async populateClaimables() {
    this.unclaimedRewards = this.orbitRewardCollection.items.filter(reward =>
      this.isRewardClaimable(reward)
    )
  }

  populateOrbitClaimBar() {
    const totalAmount = new Map<string, number>()

    const unclaimedRewards = this.unclaimedRewards

    unclaimedRewards.forEach(item => {
      if (!item.tokenPriced) return

      const tokenKey =
        item.tokenPriced.hash || item.tokenPriced.symbol || 'unknown'

      const decimals = Number(item.tokenPriced.decimals ?? 0)
      const amountInUnits = Number(item.amount || 0) / Math.pow(10, decimals)

      if (totalAmount.has(tokenKey)) {
        totalAmount.set(tokenKey, totalAmount.get(tokenKey)! + amountInUnits)
      } else {
        totalAmount.set(tokenKey, amountInUnits)
      }
    })

    const totalAmountArray: OrbitClaimBarItem[] = Array.from(
      totalAmount.entries()
    ).map(([tokenKey, amount]) => ({
      tokenImage:
        unclaimedRewards.find(item => item.tokenPriced?.hash === tokenKey)
          ?.tokenPriced?.imageUrl || '',
      tokenSymbol:
        unclaimedRewards.find(item => item.tokenPriced?.hash === tokenKey)
          ?.tokenPriced?.symbol || '',
      totalAmount: amount,
    }))

    this.orbitClaimBarItems = totalAmountArray
  }

  async populateOrbitClaimAmount() {
    const rewards: OrbitIndividualReward[] = []

    const instanceIdCache = new Map<string, number>()

    for (const item of this.unclaimedRewards) {
      if (!item.orbitProject) continue

      const projectScriptHash = item.orbitProject.contract?.hash || ''
      if (!projectScriptHash) continue

      let instanceId: number

      if (instanceIdCache.has(projectScriptHash)) {
        instanceId = instanceIdCache.get(projectScriptHash)!
      } else {
        const onChainInstanceId = await OrbitHelper.getProjectInstanceId(
          projectScriptHash
        )
        instanceId = Number(onChainInstanceId)
        instanceIdCache.set(projectScriptHash, instanceId)
      }

      rewards.push({
        instanceId: Number(instanceId),
        projectScriptHash,
        amount: Number(item.amount) || 0,
      })
    }

    this.orbitIndividualRewards = rewards
  }

  async handleRoundSelected(roundSelected: string | number) {
    const round = Number(roundSelected)
    if (isNaN(round) || round <= 0) return

    this.computedRound = round
    this.orbitProjectCollection.round = [this.computedRound]
    this.showClaimModal = false

    await this.populateResources()
  }

  @Watch('$store.state.walletAdapter.n3.address')
  async onN3AddressChange(address: string | null) {
    await this.populateTransactionCountMap(address ?? undefined)
    await this.populateRoundClaimEnd()
    await this.populateOrbitClaimData()
  }

  handleOpenClaimModal() {
    if (
      !$.walletAdapter.n3Address ||
      this.orbitClaimBarItems.length === 0 ||
      this.hasClaimPeriodEnded
    )
      return

    this.showClaimModal = true
  }

  async handleCloseClaimModal() {
    this.showClaimModal = false
    await this.populateResources()
  }
}
